import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import { Copy, EyeOff } from 'lucide-react';
import {
  Badge,
  BarChart,
  Button,
  Callout,
  cn,
  EmptyState,
  NativeSelect,
  PageHeader,
  Section,
  Skeleton,
  StatusDot,
  Switch,
  Table,
  TableContainer,
  TBody,
  Td,
  Th,
  THead,
  Tr,
  useToast,
} from '@zentera/ui';
import { api, WhatsAppText } from '@zentera/web-core';
import { usePageTitle } from '../components/shell';

/* ───────────── formatação ───────────── */

const nf = new Intl.NumberFormat('pt-BR');
const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);
function duration(ms: number | null) {
  if (ms === null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${(ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min${s % 60 ? ` ${s % 60} s` : ''}`;
  return `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`;
}
const dateTime = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const time = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
function relative(iso: string) {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `há ${h} h`;
  return dateTime(iso);
}

const SITUATION: Record<string, { label: string; tone: 'info' | 'warning' | 'success' | 'neutral' }> = {
  com_ia: { label: 'Com a IA', tone: 'info' },
  aguardando_atendente: { label: 'Aguardando atendente', tone: 'warning' },
  com_atendente: { label: 'Com atendente', tone: 'neutral' },
  resolvida_ia: { label: 'Resolvida pela IA', tone: 'success' },
};

/* ───────────── Painel ───────────── */

interface Dashboard {
  period: { days: number; since: string; includeTest: boolean; idleMinutes: number };
  totals: {
    received: number;
    handledByAi: number;
    transferred: number;
    transferredSilent: number;
    resolvedByAi: number;
    inProgress: number;
    waitingHuman: number;
    aiResolutionRate: number | null;
  };
  responseTime: { aiAvgMs: number | null; aiP50Ms: number | null; aiP95Ms: number | null; aiReplies: number; firstHumanAvgMs: number | null };
  categories: { key: string; label: string; total: number; transferred: number }[];
  handoffReasons: { reason: string; silent: boolean; total: number }[];
  daily: { day: string; received: number; transferred: number; resolved_ai: number }[];
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'warning' }) {
  return (
    <div className="px-4 py-3">
      <p className="text-xs text-fg-3">{label}</p>
      <p className={cn('tabular mt-1 text-xl font-medium text-fg', tone === 'warning' && 'text-warning')}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-fg-3">{hint}</p>}
    </div>
  );
}

export function DashboardPage() {
  usePageTitle('Painel de atendimento');
  const [days, setDays] = useState('7');
  const [includeTest, setIncludeTest] = useState(false);
  const q = useQuery({
    queryKey: ['dashboard', days, includeTest],
    queryFn: () => api.get<Dashboard>(`/api/dashboard?days=${days}&includeTest=${includeTest}`),
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-8">
      <PageHeader
        title="Painel de atendimento"
        description={`Conversas recebidas pelo assistente, resolvidas pela IA e transferidas para a equipe. Conversa sem transferência e sem mensagem nova há ${q.data?.period.idleMinutes ?? 30} minutos conta como resolvida pela IA.`}
        actions={
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-fg-2">
              <Switch label="Incluir conversas de teste" checked={includeTest} onCheckedChange={setIncludeTest} />
              Incluir testes
            </label>
            <NativeSelect aria-label="Período" value={days} onChange={(e) => setDays(e.target.value)}>
              <option value="1">Últimas 24 horas</option>
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
            </NativeSelect>
          </div>
        }
      />

      {q.isError ? (
        <Callout tone="danger" title="Não foi possível carregar o painel">Atualize a página e tente novamente.</Callout>
      ) : !q.data ? (
        <div className="space-y-6">
          <Skeleton className="h-24 rounded-lg" />
          <Skeleton className="h-56 rounded-lg" />
        </div>
      ) : q.data.totals.received === 0 ? (
        <EmptyState
          title="Nenhuma conversa no período"
          description={
            includeTest
              ? 'Ainda não há conversas, nem de teste. Converse em “Testar assistente” ou conecte seu sistema em “Integração do agente”.'
              : 'Quando o seu sistema de atendimento enviar mensagens pela Integração do agente, os números aparecem aqui. Para ver as conversas de teste, ative “Incluir testes”.'
          }
          action={
            <Button asChild variant="secondary">
              <Link to="/integration">Integração do agente</Link>
            </Button>
          }
        />
      ) : (
        <DashboardBody d={q.data} />
      )}
    </div>
  );
}

function DashboardBody({ d }: { d: Dashboard }) {
  const t = d.totals;
  const r = d.responseTime;
  return (
    <>
      <div className="grid divide-y divide-line overflow-hidden rounded-lg border border-line sm:grid-cols-3 sm:divide-y-0 lg:grid-cols-6 lg:divide-x">
        <Stat label="Recebidas" value={nf.format(t.received)} hint={`${nf.format(t.handledByAi)} respondidas pela IA`} />
        <Stat label="Resolvidas pela IA" value={nf.format(t.resolvedByAi)} hint={`${pct(t.aiResolutionRate)} das encerradas`} />
        <Stat
          label="Transferidas"
          value={nf.format(t.transferred)}
          hint={t.transferredSilent ? `${nf.format(t.transferredSilent)} silenciosas (falha da IA)` : 'nenhuma por falha da IA'}
          tone={t.transferredSilent ? 'warning' : undefined}
        />
        <Stat label="Aguardando atendente" value={nf.format(t.waitingHuman)} hint={`${nf.format(t.inProgress)} em andamento com a IA`} tone={t.waitingHuman ? 'warning' : undefined} />
        <Stat label="Resposta da IA" value={duration(r.aiAvgMs)} hint={`média · 95% até ${duration(r.aiP95Ms)}`} />
        <Stat label="1ª resposta humana" value={duration(r.firstHumanAvgMs)} hint="média após a transferência" />
      </div>

      <Section title="Conversas por dia" description="Recebidas no dia; o detalhe mostra quantas foram resolvidas pela IA e transferidas.">
        <BarChart
          ariaLabel="Conversas recebidas por dia"
          formatAxis={(v) => nf.format(Math.round(v))}
          data={d.daily.map((x) => ({
            key: x.day,
            value: x.received,
            label: new Date(`${x.day}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
            tooltip: `${nf.format(x.received)} recebidas · ${nf.format(x.resolved_ai)} resolvidas pela IA · ${nf.format(x.transferred)} transferidas`,
          }))}
        />
      </Section>

      <div className="grid gap-x-12 gap-y-8 lg:grid-cols-2">
        <Section title="Por setor" description="Setor em que o assistente tratou a conversa.">
          <TableContainer>
            <Table>
              <THead>
                <tr>
                  <Th>Setor</Th>
                  <Th align="right">Conversas</Th>
                  <Th align="right">Transferidas</Th>
                  <Th align="right">Resolvidas pela IA</Th>
                </tr>
              </THead>
              <TBody>
                {d.categories.map((c) => (
                  <Tr key={c.key}>
                    <Td>{c.label}</Td>
                    <Td align="right" className="tabular">{nf.format(c.total)}</Td>
                    <Td align="right" className="tabular">{nf.format(c.transferred)}</Td>
                    <Td align="right" className="tabular">{pct(c.total ? (c.total - c.transferred) / c.total : null)}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          </TableContainer>
        </Section>
        <Section title="Motivos de transferência">
          {d.handoffReasons.length === 0 ? (
            <p className="text-sm text-fg-3">Nenhuma transferência no período.</p>
          ) : (
            <TableContainer>
              <Table>
                <THead>
                  <tr>
                    <Th>Motivo</Th>
                    <Th align="right">Conversas</Th>
                  </tr>
                </THead>
                <TBody>
                  {d.handoffReasons.map((h, i) => (
                    <Tr key={i}>
                      <Td>
                        <span className="flex flex-wrap items-center gap-2">
                          {h.reason}
                          {h.silent && <Badge tone="warning">Silenciosa</Badge>}
                        </span>
                      </Td>
                      <Td align="right" className="tabular">{nf.format(h.total)}</Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </TableContainer>
          )}
        </Section>
      </div>
    </>
  );
}

/* ───────────── Conversas ───────────── */

interface ListItem {
  id: string;
  channel: string;
  contact: { name: string | null; phone: string | null };
  situation: string;
  categoryLabel: string | null;
  handoff: { at: string; reason: string; silent: boolean } | null;
  lastMessageAt: string;
  lastMessage: string | null;
}

interface Detail {
  conversation: {
    id: string;
    channel: string;
    contact: { name: string | null; phone: string | null; identified: boolean };
    situation: string;
    categoryLabel: string | null;
    handoff: { at: string; reason: string; silent: boolean } | null;
    firstHumanReplyAt: string | null;
    createdAt: string;
    whisper: { text: string } | null;
  };
  messages: { id: string; role: string; content: string; profileLabel: string | null; responseMs: number | null; createdAt: string }[];
}

const FILTERS: [string, string][] = [
  ['todas', 'Todas'],
  ['com_ia', 'Com a IA'],
  ['aguardando_atendente', 'Aguardando atendente'],
  ['com_atendente', 'Com atendente'],
  ['resolvida_ia', 'Resolvidas pela IA'],
  ['transferidas', 'Transferidas'],
];

export function ConversationsPage() {
  usePageTitle('Conversas');
  const { id } = useParams();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('todas');
  const [includeTest, setIncludeTest] = useState(false);
  const list = useQuery({
    queryKey: ['conversations', filter, includeTest],
    queryFn: () => api.get<{ items: ListItem[] }>(`/api/conversations?filter=${filter}&includeTest=${includeTest}`),
    refetchInterval: 15_000,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Conversas"
        description="Acompanhe o atendimento do assistente e as transferências para a equipe. As respostas dos atendentes são enviadas pelo seu sistema de atendimento."
        actions={
          <label className="flex items-center gap-2 text-sm text-fg-2">
            <Switch label="Incluir conversas de teste" checked={includeTest} onCheckedChange={setIncludeTest} />
            Incluir testes
          </label>
        }
      />
      <div role="tablist" aria-label="Filtro" className="flex flex-wrap gap-1">
        {FILTERS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={filter === k}
            onClick={() => setFilter(k)}
            className={cn('rounded-md px-2.5 py-1 text-sm', filter === k ? 'bg-muted font-medium text-fg' : 'text-fg-3 hover:text-fg')}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
        <div className="min-w-0">
          {list.isError ? (
            <Callout tone="danger" title="Não foi possível carregar as conversas">Atualize a página.</Callout>
          ) : !list.data ? (
            <Skeleton className="h-96 rounded-lg" />
          ) : list.data.items.length === 0 ? (
            <EmptyState compact title="Nenhuma conversa" description={includeTest ? 'Nada com este filtro.' : 'Ative “Incluir testes” para ver as conversas de teste.'} />
          ) : (
            <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
              {list.data.items.map((c) => {
                const s = SITUATION[c.situation] ?? SITUATION.com_ia!;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => void navigate(`/conversations/${c.id}`)}
                      aria-current={c.id === id ? 'true' : undefined}
                      className={cn('block w-full px-3 py-2.5 text-left hover:bg-subtle', c.id === id && 'bg-subtle')}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-fg">{c.contact.name ?? c.contact.phone ?? 'Cliente'}</span>
                        <span className="shrink-0 text-xs text-fg-4">{relative(c.lastMessageAt)}</span>
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                        <StatusDot tone={s.tone}>{s.label}</StatusDot>
                        {c.categoryLabel && <span className="text-fg-3">{c.categoryLabel}</span>}
                        {c.handoff?.silent && (
                          <span className="inline-flex items-center gap-1 text-warning">
                            <EyeOff className="size-3" /> silenciosa
                          </span>
                        )}
                        {c.channel === 'test' && <Badge>Teste</Badge>}
                      </span>
                      {c.lastMessage && <span className="mt-1 block truncate text-xs text-fg-3">{c.lastMessage}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="min-w-0">{id ? <ConversationDetail id={id} /> : <EmptyState compact title="Selecione uma conversa" description="O histórico completo e o sussurro da transferência aparecem aqui." />}</div>
      </div>
    </div>
  );
}

function ConversationDetail({ id }: { id: string }) {
  const toast = useToast();
  const q = useQuery({ queryKey: ['conversation', id], queryFn: () => api.get<Detail>(`/api/conversations/${id}`), refetchInterval: 10_000 });
  if (q.isError) return <Callout tone="danger" title="Conversa não encontrada">Ela pode ter sido removida.</Callout>;
  if (!q.data) return <Skeleton className="h-96 rounded-lg" />;
  const c = q.data.conversation;
  const s = SITUATION[c.situation] ?? SITUATION.com_ia!;

  return (
    <article className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line pb-4">
        <div>
          <h2 className="text-base font-medium text-fg">{c.contact.name ?? 'Cliente não identificado'}</h2>
          <p className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-fg-3">
            {c.contact.phone && <span className="tabular">{c.contact.phone}</span>}
            <span>Início {dateTime(c.createdAt)}</span>
            {c.categoryLabel && <span>{c.categoryLabel}</span>}
            {c.channel === 'test' && <span>Conversa de teste</span>}
          </p>
        </div>
        <StatusDot tone={s.tone}>{s.label}</StatusDot>
      </header>

      {c.handoff && (
        <Callout
          tone={c.handoff.silent ? 'warning' : 'info'}
          title={c.handoff.silent ? `Transferência silenciosa · ${dateTime(c.handoff.at)}` : `Transferida para atendente · ${dateTime(c.handoff.at)}`}
        >
          <p>
            {c.handoff.reason}
            {c.handoff.silent && '. O cliente recebeu a mensagem normal de transferência e não soube da falha.'}
            {c.firstHumanReplyAt
              ? ` Primeira resposta do atendente em ${duration(new Date(c.firstHumanReplyAt).getTime() - new Date(c.handoff.at).getTime())}.`
              : ' Ainda sem resposta do atendente.'}
          </p>
          {c.whisper && (
            <div className="mt-3">
              <p className="mb-1 flex items-center gap-2 text-xs font-medium text-fg-2">
                Sussurro entregue ao atendente
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-xs font-normal text-fg-3 underline-offset-2 hover:underline"
                  onClick={() => {
                    void navigator.clipboard.writeText(c.whisper!.text);
                    toast({ tone: 'success', title: 'Sussurro copiado' });
                  }}
                >
                  <Copy className="size-3" /> copiar
                </button>
              </p>
              <pre className="whitespace-pre-wrap rounded-md border border-line bg-bg p-3 font-sans text-sm text-fg-2">{c.whisper.text}</pre>
            </div>
          )}
        </Callout>
      )}

      <ol className="space-y-3" aria-label="Mensagens">
        {q.data.messages.map((m) => {
          if (m.role === 'note') {
            return (
              <li key={m.id} className="flex justify-center">
                <span className="rounded-md border border-dashed border-line px-3 py-1 text-xs text-fg-3">Nota interna: sussurro gerado na transferência · {time(m.createdAt)}</span>
              </li>
            );
          }
          const customer = m.role === 'customer';
          return (
            <li key={m.id} className={cn('flex', customer ? 'justify-start' : 'justify-end')}>
              <div className={cn('max-w-[80%] rounded-lg border px-3 py-2', customer ? 'border-line bg-bg' : m.role === 'agent' ? 'border-line bg-subtle' : 'border-brand/20 bg-brand/5')}>
                <p className="mb-1 flex flex-wrap items-center gap-2 text-2xs text-fg-3">
                  <span className="font-medium text-fg-2">{customer ? 'Cliente' : m.role === 'agent' ? 'Atendente' : 'Assistente'}</span>
                  {m.role === 'assistant' && m.profileLabel && <span>{m.profileLabel}</span>}
                  <span>{time(m.createdAt)}</span>
                  {m.responseMs !== null && <span>respondeu em {duration(m.responseMs)}</span>}
                </p>
                <div className="text-sm text-fg"><WhatsAppText text={m.content} /></div>
              </div>
            </li>
          );
        })}
      </ol>
    </article>
  );
}
